import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Calendar, Clock, MapPin, Users } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { SessionProposal, getSessionProposals, registerDogToSession, unregisterDogFromSession } from "../../utils/api";
import { useEventStream } from "../../utils/useEventStream";

const ACTIVITY_LABELS: Record<string, string> = {
  mantrailing: "Mantrailing",
  hiking: "Randonnée",
  canicross: "Canicross",
  detection: "Détection",
};

const selectClass = "h-9 rounded-md border bg-background px-2 text-sm";

export const ProposalsPage: React.FC = () => {
  const [proposals, setProposals] = useState<SessionProposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [activity, setActivity] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setProposals(await getSessionProposals({ activityType: activity || undefined, q: query || undefined }));
    } catch {
      toast.error("Impossible de charger les sessions proposées.");
    } finally {
      setLoading(false);
    }
  }, [activity, query]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  useEventStream(undefined, {
    "registration:promoted": () => { toast.success("Une place s'est libérée : votre chien est inscrit !"); load(); },
    "session:cancelled": () => { toast.info("Une session a été annulée."); load(); },
    "registration:changed": () => load(),
  });

  const grouped = useMemo(() => {
    const byDay = new Map<string, SessionProposal[]>();
    proposals.forEach(p => {
      const day = new Date(p.startTime).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
      byDay.set(day, [...(byDay.get(day) || []), p]);
    });
    return Array.from(byDay.entries());
  }, [proposals]);

  const register = async (session: SessionProposal, dogId: string) => {
    setBusyId(session._id + dogId);
    try {
      const { status } = await registerDogToSession(session._id, dogId);
      toast.success(status === "waitlisted" ? "Session complète : vous êtes sur liste d'attente." : "Inscription confirmée !");
      await load();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Inscription impossible.");
    } finally {
      setBusyId(null);
    }
  };

  const unregister = async (session: SessionProposal, dogId: string) => {
    setBusyId(session._id + dogId);
    try {
      await unregisterDogFromSession(session._id, dogId);
      toast.success("Inscription retirée.");
      await load();
    } catch {
      toast.error("Impossible de retirer l'inscription.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-4">
      <div>
        <h1 className="text-2xl font-bold">Sessions proposées</h1>
        <p className="text-sm text-muted-foreground">Inscrivez vos chiens aux sessions publiées par vos éducateurs.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Input className="max-w-xs" placeholder="Rechercher (lieu, groupe, titre)…" value={query} onChange={e => setQuery(e.target.value)} />
        <select className={selectClass} value={activity} onChange={e => setActivity(e.target.value)} aria-label="Activité">
          <option value="">Toutes les activités</option>
          {Object.entries(ACTIVITY_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </div>

      {loading ? (
        <p className="text-muted-foreground">Chargement…</p>
      ) : grouped.length === 0 ? (
        <p className="rounded-md border border-dashed p-6 text-center text-muted-foreground">Aucune session publiée pour le moment.</p>
      ) : (
        grouped.map(([day, sessions]) => (
          <section key={day} className="space-y-2">
            <h2 className="text-sm font-semibold capitalize text-muted-foreground">{day}</h2>
            {sessions.map(session => {
              const full = session.maxDogs !== null && session.confirmedCount >= session.maxDogs;
              return (
                <Card key={session._id} style={{ borderLeft: `6px solid ${session.dashboardColor || "#3b82f6"}` }}>
                  <CardHeader className="pb-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <CardTitle className="text-base">{session.title || session.dashboardTitle}</CardTitle>
                      <div className="flex gap-2">
                        <Badge>{ACTIVITY_LABELS[session.activityType] || session.activityType}</Badge>
                        <Badge variant={full ? "destructive" : "secondary"}>
                          <Users className="mr-1 h-3 w-3" />
                          {session.confirmedCount}{session.maxDogs !== null ? `/${session.maxDogs}` : ""}{full ? " · complet" : ""}
                        </Badge>
                        {session.waitlistCount > 0 && <Badge variant="outline"><Clock className="mr-1 h-3 w-3" />{session.waitlistCount} en attente</Badge>}
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">{session.dashboardTitle}{session.trainers.length ? ` · ${session.trainers.join(", ")}` : ""}</p>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1"><Calendar className="h-4 w-4" />
                        {new Date(session.startTime).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })} – {new Date(session.endTime).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                      </span>
                      {session.location && (
                        <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />
                          {session.locationCoordinate ? (
                            <a className="underline" target="_blank" rel="noreferrer" href={`https://www.openstreetmap.org/?mlat=${session.locationCoordinate.coordinates[1]}&mlon=${session.locationCoordinate.coordinates[0]}#map=16/${session.locationCoordinate.coordinates[1]}/${session.locationCoordinate.coordinates[0]}`}>{session.location}</a>
                          ) : session.location}
                        </span>
                      )}
                    </div>
                    {session.comment && <p className="text-sm">{session.comment}</p>}
                    <div className="flex flex-wrap gap-2">
                      {session.eligibleDogs.map(dog => {
                        const mine = session.myRegistrations.find(r => r.dogId === dog._id);
                        const busy = busyId === session._id + dog._id;
                        return mine ? (
                          <Button key={dog._id} size="sm" variant="outline" disabled={busy} onClick={() => unregister(session, dog._id)}>
                            {dog.name} · {mine.status === "waitlisted" ? "en attente" : "inscrit"} (retirer)
                          </Button>
                        ) : (
                          <Button key={dog._id} size="sm" disabled={busy} onClick={() => register(session, dog._id)}>
                            {full ? `Liste d'attente : ${dog.name}` : `Inscrire ${dog.name}`}
                          </Button>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </section>
        ))
      )}
    </div>
  );
};
